import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { orderService } from '../services/order.service';
import { isAdmin } from '../utils/permissions';
import dayjs from 'dayjs';
// Async thunks
export const fetchOrders = createAsyncThunk(
  'order/fetchOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await orderService.getAllOrders();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des commandes');
    }
  }
);

export const fetchActiveOrders = createAsyncThunk(
  'order/fetchActiveOrders',
  async (_, { rejectWithValue }) => {
    try {
      return await orderService.getActiveOrders();
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des commandes actives');
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'order/fetchOrderById',
  async (id, { rejectWithValue }) => {
    try {
      return await orderService.getOrderById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération de la commande');
    }
  }
);

export const fetchOrdersByShop = createAsyncThunk(
  'order/fetchOrdersByShop',
  async (shopId, { rejectWithValue }) => {
    try {
      return await orderService.getOrdersByShop(shopId);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des commandes du magasin');
    }
  }
);

export const fetchOrdersByCustomer = createAsyncThunk(
  'order/fetchOrdersByCustomer',
  async (customerId, { rejectWithValue }) => {
    try {
      return await orderService.getOrdersByCustomer(customerId);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des commandes du client');
    }
  }
);

export const fetchOrdersByLab = createAsyncThunk(
  'order/fetchOrdersByLab',
  async (labId, { rejectWithValue }) => {
    try {
      return await orderService.getOrdersByLab(labId);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des commandes du laboratoire');
    }
  }
);

export const fetchOrdersByType = createAsyncThunk(
  'order/fetchOrdersByType',
  async (typeId, { rejectWithValue }) => {
    try {
      // Fetch all orders and filter by type on frontend
      const response = await orderService.getAllOrders();
      const allOrders = Array.isArray(response) ? response : (response?.items || []);
      // Filter by orderTypeId
      const filteredOrders = allOrders.filter(order => order.orderTypeId === typeId);
      return filteredOrders;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des commandes par type');
    }
  }
);

export const searchOrders = createAsyncThunk(
  'order/searchOrders',
  async (query, { rejectWithValue }) => {
    try {
      return await orderService.searchOrders(query);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la recherche de commandes');
    }
  }
);

export const addOrder = createAsyncThunk(
  'order/addOrder',
  async (order, { rejectWithValue }) => {
    try {
      const result = await orderService.createOrder(order);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la création de la commande');
    }
  }
);

export const editOrder = createAsyncThunk(
  'order/editOrder',
  async ({ id, order }, { rejectWithValue }) => {
    try {
      const result = await orderService.updateOrder(id, order);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la mise à jour de la commande');
    }
  }
);

export const removeOrder = createAsyncThunk(
  'order/removeOrder',
  async (id, { rejectWithValue }) => {
    try {
      await orderService.deleteOrder(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la suppression de la commande');
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'order/cancelOrder',
  async (id, { rejectWithValue }) => {
    try {
      await orderService.cancelOrder(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de l'annulation de la commande");
    }
  }
);

export const transformOrderToSale = createAsyncThunk(
  'order/transformOrderToSale',
  async (id, { rejectWithValue }) => {
    try {
      const result = await orderService.transformOrderToSale(id);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la transformation en vente');
    }
  }
);

export const generateTomorrowShopOrders = createAsyncThunk(
  'order/generateTomorrowShopOrders',
  async (shopId, { rejectWithValue }) => {
    try {
      const result = await orderService.generateTomorrowShopOrders(shopId);
      return result; // ton endpoint renvoie OrderReadDTO
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Erreur lors de la génération des commandes boutique de demain"
      );
    }
  }
);

export const fetchOrdersByDateRange = createAsyncThunk(
  'order/fetchOrdersByDateRange',
  async (filter, { rejectWithValue }) => {
    try {
      return await orderService.getOrdersByDateRange(filter);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Erreur lors de la récupération des commandes par date'
      );
    }
  }
);

export const fetchOrdersByDeliveryDateRange = createAsyncThunk(
  'order/fetchOrdersByDeliveryDateRange',
  async (filter, { rejectWithValue }) => {
    try {
      return await orderService.getOrdersByDeliveryDateRange(filter);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Erreur lors de la récupération des commandes par date de livraison'
      );
    }
  }
);

export const fetchShopOrdersByDateRange = createAsyncThunk(
  'order/fetchShopOrdersByDateRange',
  async (filter, { rejectWithValue }) => {
    try {
      return await orderService.getShopOrdersByDateRange(filter);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Erreur lors de la récupération des commandes boutique par date'
      );
    }
  }
);

export const fetchShopOrdersByDeliveryDateRange = createAsyncThunk(
  'order/fetchShopOrdersByDeliveryDateRange',
  async (filter, { rejectWithValue }) => {
    try {
      return await orderService.getShopOrdersByDeliveryDateRange(filter);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Erreur lors de la récupération des commandes boutique par date de livraison'
      );
    }
  }
);

export const generateShopOrderByDate = createAsyncThunk(
  'order/generateShopOrderByDate',
  async ({ shopId, orderDate }, { rejectWithValue }) => {
    try {
      const result = await orderService.generateShopOrderByDate(shopId, orderDate);
      return result; // OrderReadDTO
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors de la génération de la commande boutique pour la date choisie'
      );
    }
  }
);

export const editOrderAsync = createAsyncThunk(
  'order/editOrderAsync',
  async ({ id, order }, { dispatch, rejectWithValue }) => {
    try {
      const result = await orderService.updateOrderAsync(id, order);

      dispatch(
        orderUpdateStarted({
          jobId: result.jobId,
          orderId: result.orderId ?? id,
        })
      );

      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Erreur lors du lancement de la mise à jour de la commande'
      );
    }
  }
);

export const generateTomorrowShopOrdersAsync = createAsyncThunk(
  'order/generateTomorrowShopOrdersAsync',
  async (shopId, { dispatch, rejectWithValue }) => {
    try {
      const result = await orderService.generateTomorrowShopOrdersAsync(shopId);

      dispatch(
        orderGenerateStarted({
          jobId: result.jobId,
          mode: 'generateTomorrow',
          shopId,
        })
      );

      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Erreur lors du lancement de la génération des commandes boutique de demain"
      );
    }
  }
);

export const generateShopOrderByDateAsync = createAsyncThunk(
  'order/generateShopOrderByDateAsync',
  async ({ shopId, orderDate }, { dispatch, rejectWithValue }) => {
    try {
      const result = await orderService.generateShopOrderByDateAsync(shopId, orderDate);

      dispatch(
        orderGenerateStarted({
          jobId: result.jobId,
          mode: 'generateByDate',
          shopId,
          orderDate,
        })
      );

      return result;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors du lancement de la génération de la commande boutique'
      );
    }
  }
);

export const generateShopOrderAnnexByDate = createAsyncThunk(
  'order/generateShopOrderAnnexByDate',
  async ({ shopId, orderDate, forceEmptyAnnex = false }, { rejectWithValue }) => {
    try {
      const result = await orderService.generateShopOrderAnnexByDate(
        shopId,
        orderDate,
        forceEmptyAnnex
      );
      return result;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ?? {
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Erreur lors de la génération de l'annexe de commande boutique",
          code: error?.response?.data?.code || null,
        }
      );
    }
  }
);

export const selectPendingGenerateTomorrowJobForShop = (state, shopId) => {
  const jobs = Object.values(state.order.generateJobs || {});
  return jobs.find(
    (job) =>
      job?.status === 'pending' &&
      job?.type === 'generateTomorrow' &&
      String(job?.shopId) === String(shopId)
  );
};

export const selectPendingGenerateByDateJobForShopAndDate = (state, shopId, orderDate) => {
  if (!orderDate) return null;

  const targetDate = dayjs(orderDate).startOf('day').toISOString();
  const jobs = Object.values(state.order.generateJobs || {});

  return jobs.find(
    (job) =>
      job?.status === 'pending' &&
      job?.type === 'generateByDate' &&
      String(job?.shopId) === String(shopId) &&
      dayjs(job?.orderDate).startOf('day').toISOString() === targetDate
  );
};

export const selectAnyPendingGenerateJobForShop = (state, shopId) => {
  const jobs = Object.values(state.order.generateJobs || {});
  return jobs.find(
    (job) =>
      job?.status === 'pending' &&
      String(job?.shopId) === String(shopId)
  );
};

export const fetchLabProductionRange = createAsyncThunk(
  'order/fetchLabProductionRange',
  async (filter, { rejectWithValue }) => {
    try {
      return await orderService.getLabProductionRange(filter);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Erreur lors de la récupération des besoins de production labo'
      );
    }
  }
);

export const pullOrdersFromSite = createAsyncThunk(
  'order/pullOrdersFromSite',
  async (filter = {}, { rejectWithValue }) => {
    try {
      return await orderService.pullOrdersFromSite(filter);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || { message: error.message || 'Erreur de synchronisation' }
      );
    }
  }
);
// Initial state
const initialState = {
  orders: [],
  totalCount: 0,
  searchResults: [],
  selectedOrder: null,
  loading: false,
  searchLoading: false,
  error: null,
  showModal: false,
  showDeleteModal: false,
  searchQuery: '',
  orderType: null, // 0 = customer order, 1 = shop order
  updateJobs: {},   // { [jobId]: { type, orderId, status, error, result } }
  generateJobs: {}, // { [jobId]: { type, shopId, orderDate, status, error, result}}
  labProductionRange: [],
  labProductionTotalCount: 0,
  labProductionLoading: false,
};

// Slice
const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedOrder: (state, action) => {
      state.selectedOrder = action.payload;
    },
    setOrderType: (state, action) => {
      state.orderType = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchQuery = '';
    },

    orderUpdateStarted: (state, action) => {
      const { jobId, orderId } = action.payload;
      state.updateJobs[jobId] = {
        type: 'update',
        orderId,
        status: 'pending',
        error: null,
        result: null,
      };
    },

    orderUpdateSuccess: (state, action) => {
      const { jobId, orderId, result } = action.payload;

      if (jobId) {
        state.updateJobs[jobId] = {
          ...(state.updateJobs[jobId] || {}),
          type: 'update',
          orderId,
          status: 'success',
          error: null,
          result,
        };
      }

      if (result) {
        const resultOrderId = result.orderId ?? result.id ?? orderId;

        const index = state.orders.findIndex(
          (order) => (order.orderId ?? order.id) === resultOrderId
        );

        if (index !== -1) {
          state.orders[index] = result;
        } else {
          state.orders.unshift(result);
          state.totalCount += 1;
        }

        if (
          state.selectedOrder &&
          (state.selectedOrder.orderId ?? state.selectedOrder.id) === resultOrderId
        ) {
          state.selectedOrder = result;
        }
      }

      state.loading = false;
      state.showModal = false;
    },

    orderUpdateFailed: (state, action) => {
      const { jobId, orderId, error } = action.payload;

      if (jobId) {
        state.updateJobs[jobId] = {
          ...(state.updateJobs[jobId] || {}),
          type: 'update',
          orderId,
          status: 'failed',
          error,
          result: null,
        };
      }

      state.loading = false;
      state.error = error;
    },

    orderGenerateStarted: (state, action) => {
      const { jobId, mode, shopId, orderDate } = action.payload;

      state.generateJobs[jobId] = {
        type: mode,
        shopId,
        orderDate: orderDate ?? null,
        status: 'pending',
        error: null,
        result: null,
      };
    },

    orderGenerateSuccess: (state, action) => {
      const { jobId, result, mode, shopId, orderDate } = action.payload;

      if (jobId) {
        state.generateJobs[jobId] = {
          ...(state.generateJobs[jobId] || {}),
          type: mode,
          shopId,
          orderDate: orderDate ?? null,
          status: 'success',
          error: null,
          result,
        };
      }

      const generatedOrders = Array.isArray(result) ? result : result ? [result] : [];

      generatedOrders.forEach((newOrder) => {
        const newOrderId = newOrder.orderId ?? newOrder.id;
        const exists = state.orders.some(
          (o) => (o.orderId ?? o.id) === newOrderId
        );

        if (!exists) {
          state.orders.unshift(newOrder);
          state.totalCount += 1;
        } else {
          const idx = state.orders.findIndex(
            (o) => (o.orderId ?? o.id) === newOrderId
          );
          if (idx !== -1) state.orders[idx] = newOrder;
        }
      });

      state.loading = false;
    },

    orderGenerateFailed: (state, action) => {
      const { jobId, mode, shopId, orderDate, error } = action.payload;

      if (jobId) {
        state.generateJobs[jobId] = {
          ...(state.generateJobs[jobId] || {}),
          type: mode,
          shopId,
          orderDate: orderDate ?? null,
          status: 'failed',
          error,
          result: null,
        };
      }

      state.loading = false;
      state.error = error;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch active orders
      .addCase(fetchActiveOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchActiveOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch order by ID
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch orders by shop
      .addCase(fetchOrdersByShop.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrdersByShop.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchOrdersByShop.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch orders by customer
      .addCase(fetchOrdersByCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrdersByCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchOrdersByCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch orders by lab
      .addCase(fetchOrdersByLab.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrdersByLab.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchOrdersByLab.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch orders by type
      .addCase(fetchOrdersByType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrdersByType.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchOrdersByType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Search orders
      .addCase(searchOrders.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchOrders.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
      })
      .addCase(searchOrders.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload;
      })

      // Add order
      .addCase(addOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders.push(action.payload);
        state.totalCount += 1;
        state.showModal = false;
      })
      .addCase(addOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Edit order
      .addCase(editOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = state.orders.map((order) => {
          if (order.id === action.payload.id) return action.payload;
          return order;
        });
        state.showModal = false;
      })
      .addCase(editOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Remove order
      .addCase(removeOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = state.orders.filter((order) => order.id !== action.payload);
        state.totalCount -= 1;
        state.showDeleteModal = false;
      })
      .addCase(removeOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Cancel order
      .addCase(cancelOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = state.orders.filter((order) => order.orderId !== action.payload);
        state.totalCount -= 1;
        state.showDeleteModal = false;
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Transform order to sale
      .addCase(transformOrderToSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transformOrderToSale.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the transformed order from the list
        state.orders = state.orders.filter((order) => order.orderId !== action.payload.orderId);
        state.totalCount -= 1;
      })
      .addCase(transformOrderToSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Generate tomorrow shop orders
      .addCase(generateTomorrowShopOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateTomorrowShopOrders.fulfilled, (state, action) => {
        state.loading = false;
        // On ajoute la commande générée dans la liste si elle n'y est pas déjà
        const newOrder = action.payload;
        if (newOrder && !state.orders.some(o => o.orderId === newOrder.orderId)) {
          state.orders.push(newOrder);
          state.totalCount += 1;
        }
      })
      .addCase(generateTomorrowShopOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOrdersByDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrdersByDateRange.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);

      })
      .addCase(fetchOrdersByDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchOrdersByDeliveryDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrdersByDeliveryDateRange.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchOrdersByDeliveryDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchShopOrdersByDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShopOrdersByDateRange.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.Items || action.payload?.items || [];
      })
      .addCase(fetchShopOrdersByDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchShopOrdersByDeliveryDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShopOrdersByDeliveryDateRange.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.Items || action.payload?.items || [];
      })
      .addCase(fetchShopOrdersByDeliveryDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(generateShopOrderByDate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateShopOrderByDate.fulfilled, (state, action) => {
        state.loading = false;
        const newOrder = action.payload;
        // On ajoute la commande générée dans la liste si elle n'y est pas déjà
        if (
          newOrder &&
          !state.orders.some((o) => o.orderId === newOrder.orderId)
        ) {
          state.orders.push(newOrder);
          state.totalCount += 1;
        }
      })
      .addCase(generateShopOrderByDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(editOrderAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editOrderAsync.fulfilled, (state) => {
        state.loading = true; // le job continue côté backend
      })
      .addCase(editOrderAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(generateTomorrowShopOrdersAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateTomorrowShopOrdersAsync.fulfilled, (state) => {
        state.loading = true; // job lancé, on attend SignalR
      })
      .addCase(generateTomorrowShopOrdersAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(generateShopOrderByDateAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateShopOrderByDateAsync.fulfilled, (state) => {
        state.loading = true; // job lancé
      })
      .addCase(generateShopOrderByDateAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(generateShopOrderAnnexByDate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateShopOrderAnnexByDate.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const newOrder = action.payload;

        if (!newOrder) {
          return;
        }

        const newOrderId = newOrder.orderId ?? newOrder.id;
        const idx = state.orders.findIndex(
          (o) => (o.orderId ?? o.id) === newOrderId
        );

        if (idx === -1) {
          state.orders.unshift(newOrder);
          state.totalCount += 1;
        } else {
          state.orders[idx] = newOrder;
        }
      })
      .addCase(generateShopOrderAnnexByDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchLabProductionRange.pending, (state) => {
        state.labProductionLoading = true;
        state.error = null;
      })
      .addCase(fetchLabProductionRange.fulfilled, (state, action) => {
        state.labProductionLoading = false;
        state.labProductionRange = Array.isArray(action.payload)
          ? action.payload
          : (action.payload?.items || []);
        state.labProductionTotalCount =
          action.payload?.totalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchLabProductionRange.rejected, (state, action) => {
        state.labProductionLoading = false;
        state.error = action.payload;
      })

      .addCase(pullOrdersFromSite.pending, (state) => {
        state.loading = true;
      })
      .addCase(pullOrdersFromSite.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(pullOrdersFromSite.rejected, (state) => {
        state.loading = false;
      })
      ;


  },
});

export const {
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  clearError,
  setSelectedOrder,
  setOrderType,
  setSearchQuery,
  clearSearchResults,
  orderUpdateStarted,
  orderUpdateSuccess,
  orderUpdateFailed,
  orderGenerateStarted,
  orderGenerateSuccess,
  orderGenerateFailed,
} = orderSlice.actions;

// Selectors
/**
 * Selector: Get customer orders accessible to the user
 * - Utilisable avec tous les thunks qui remplissent state.order.orders
 * - Filtre par type client (orderTypeId === 0 si dispo)
 * - Filtre par shops pour les non-admins
 */
export const selectAccessibleCustomerOrders = createSelector(
  [(state) => state.order.orders, (state) => state.auth.user],
  (allOrders, currentUser) => {
    if (!currentUser) return [];

    // 1) Si les commandes ont un orderTypeId, on filtre sur 0 (customer)
    //    Sinon, on considère que la liste reçue est déjà filtrée côté backend
    const hasOrderTypeField = allOrders.some(o => o.orderTypeId !== undefined && o.orderTypeId !== null);

    const customerOrders = hasOrderTypeField
      ? allOrders.filter(order => order.orderTypeId === 0)
      : allOrders;

    // 2) ADMIN → voit toutes les commandes client
    if (isAdmin(currentUser)) {
      return customerOrders;
    }

    // 3) Non-admin → uniquement les shops auquel il a accès
    if (Array.isArray(currentUser.shopIds) && currentUser.shopIds.length > 0) {
      return customerOrders.filter(order =>
        currentUser.shopIds.includes(order.shopId)
      );
    }

    // Si pas de shopIds, par sécurité on renvoie une liste vide
    return [];
  }
);
/**
 * Selector: Get shop orders accessible to the user
 * Filters orders where user has access to the shop
 */
export const selectAccessibleShopOrders = createSelector(
  [(state) => state.order.orders, (state) => state.auth.user],
  (allOrders, currentUser) => {
    if (!currentUser) return [];

    const hasOrderTypeField = allOrders.some(
      (o) => o.orderTypeId !== undefined && o.orderTypeId !== null
    );

    const shopOrders = hasOrderTypeField
      ? allOrders.filter((order) => order.orderTypeId === 1)
      : allOrders;

    if (isAdmin(currentUser)) {
      return shopOrders;
    }

    if (Array.isArray(currentUser.shopIds) && currentUser.shopIds.length > 0) {
      return shopOrders.filter((order) => currentUser.shopIds.includes(order.shopId));
    }

    return [];
  }
);

export const selectPendingOrderUpdateJobByOrderId = createSelector(
  [
    (state) => state.order.updateJobs,
    (_, orderId) => String(orderId),
  ],
  (updateJobs, orderId) => {
    const jobs = Object.values(updateJobs || {});
    return (
      jobs.find(
        (job) =>
          job?.status === 'pending' &&
          String(job?.orderId) === orderId
      ) || null
    );
  }
);

export const selectIsOrderUpdating = createSelector(
  [
    selectPendingOrderUpdateJobByOrderId,
  ],
  (job) => !!job
);

export const selectLabProductionRange = (state) => state.order.labProductionRange;
export const selectLabProductionTotalCount = (state) => state.order.labProductionTotalCount;
export const selectLabProductionLoading = (state) => state.order.labProductionLoading;

export default orderSlice.reducer;
